import { generateFramedWall } from "../domain/framing/generateFramedWall";
import { calculateWallStudCuts } from "../domain/framing/wallStudCuts";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import { freeWalls, normalizeIndoorRoom } from "../domain/indoor/indoorRoom";
import { calculateOpeningTrimTakeoff } from "../domain/openings/calculateOpeningTrimTakeoff";
import { validateWallOpenings } from "../domain/openings/validateOpeningSet";
import { createRectRoofGeometry } from "../domain/roof/rectRoofGeometry";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import { resolveOpeningAdjustmentOpenings } from "./twoRoomOpeningConfig";
import type {
	BuildInput,
	CalculationResultDraft,
	CutRow,
	MaterialRow,
} from "./types";

const STUD_LABELS: Record<string, string> = {
	king_stud: "King stud",
	jack_stud: "Jack stud",
	cripple_top: "Cripple stud (topp)",
	cripple_bottom: "Cripple stud (botten)",
};

function wallLengthMm(
	wall: "front" | "back" | "left" | "right" | "divider",
	input: BuildInput,
): number {
	if (wall === "front" || wall === "back") {
		return Math.round(input.dimensions.width * 1000);
	}
	if (wall === "divider") {
		return Math.max(0, Math.round(input.dimensions.width * 1000) - 190);
	}
	return Math.round(input.dimensions.length * 1000);
}

/**
 * Doors and windows are products, and the engines used to bill them as fixed
 * `1 st` rows written into the BOM before any opening was known. The model draws
 * a door only where a door OPENING exists, so the two disagreed for every design
 * without one: the material list — and the price — carried a door the build had
 * nowhere to fit. The square engine had the inverse problem, listing `Fönsterfoder`
 * (the trim) while never selling the glazing it framed.
 *
 * Keyed on `materialKey`, never on the Swedish label: the labels are user-facing
 * and get translated, the keys are stable identifiers.
 */
const OPENING_PRODUCT_COUNTS: Record<string, (input: BuildInput) => number> = {
	"systems.door.sauna": ({ openings }) =>
		openings.filter((o) => o.type === "door" && o.wall !== "divider").length,
	"systems.door.exterior": ({ openings }) =>
		openings.filter((o) => o.type === "door" && o.wall !== "divider").length,
	"systems.window": (input) => {
		// Panorama replaces the right wall's window with a full-wall glazed surface
		// generated from `glazingStyle` — it is never an entry in `openings`, and
		// `resolveOpeningAdjustmentOpenings` even strips the right-wall windows for
		// that reason. The two-room engine sells that glass by the square metre on
		// its own `Panoramaglas` row, so only the placed windows count here: the
		// glass wall used to ride along as one more 2 200 kr window.
		const isPanorama = input.glazingStyle === "panorama";
		return input.openings.filter(
			(o) => o.type === "window" && !(isPanorama && o.wall === "right"),
		).length;
	},
	// NOT `systems.door.interior`. The two-room mellandörr is generated by the
	// partition, not placed as an opening: a default two-room design draws four
	// `detail/divider-door/*` meshes while `input.openings` holds only the front
	// door. Counting it here would drop a row for a door that is really there.
	// Its presence is already governed by the relax-room config.
	//
	// The rule for this whole table: a product may only be counted from
	// `openings` when the builder ALSO draws it from `openings`. Anything the
	// engine generates from config belongs outside it.
};

/** The glazing row the square engine never had. Priced like the two-room one. */
const SQUARE_WINDOW_ROW: Omit<MaterialRow, "quantity"> = {
	material: "Fönster",
	dimension: "Isolerglas",
	phase: "systems",
	priceRuleId: "prices.fonster",
	materialKey: "systems.window",
};

function reconcileOpeningProductRows(
	base: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	const materials = base.materials.flatMap((row) => {
		const counter = row.materialKey && OPENING_PRODUCT_COUNTS[row.materialKey];
		if (!counter) return [row];
		const count = counter(input);
		// No opening, no product — drop the row rather than bill a phantom door.
		if (count === 0) return [];
		return [{ ...row, quantity: `${count} st` }];
	});

	// Glazing that is drawn but was never sold. Only add it when the engine does
	// not already carry a window row (two-room does).
	const windowCount = OPENING_PRODUCT_COUNTS["systems.window"](input);
	const hasWindowRow = materials.some(
		(row) => row.materialKey === "systems.window",
	);
	if (windowCount > 0 && !hasWindowRow) {
		materials.push({ ...SQUARE_WINDOW_ROW, quantity: `${windowCount} st` });
	}

	return { ...base, materials };
}

export function applyOpeningAdjustments(
	base: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	// Runs before the early return below: a design with NO openings still needs
	// its unconditional door row removed.
	let reconciled = reconcileOpeningProductRows(base, input);
	// Two-room already derives standard cuts from its glazing-aware frame plan.
	if (!base.config && input.roofConfig?.kind !== "barrel") {
		const cuts = calculateWallStudCuts(input);
		const lengths = (rows: CutRow[]) =>
			rows
				.flatMap((c) => c.pieces ?? [])
				.reduce((sum, p) => sum + (p.lengthMm * p.count) / 1000, 0);
		const oldLength = lengths(
			reconciled.cuts.filter((c) =>
				c.materialKey?.startsWith("framing.studs."),
			),
		);
		reconciled = {
			...reconciled,
			materials: reconciled.materials.map((row) =>
				row.materialKey === "framing.wall-studs"
					? {
							...row,
							quantity: `${Math.ceil(Number.parseFloat(row.quantity) - oldLength + lengths(cuts))} lpm`,
						}
					: row,
			),
			cuts: [
				...reconciled.cuts.filter(
					(c) => !c.materialKey?.startsWith("framing.studs."),
				),
				...cuts,
			],
		};
	}

	const openingsForAdjustment = resolveOpeningAdjustmentOpenings(input).filter(
		(o) => o.wall !== "divider",
	);
	if (openingsForAdjustment.length === 0) {
		return reconciled;
	}
	base = reconciled;

	const grouped = new Map<
		"front" | "back" | "left" | "right" | "divider",
		typeof input.openings
	>();
	for (const opening of openingsForAdjustment) {
		const list = grouped.get(opening.wall) ?? [];
		list.push(opening);
		grouped.set(opening.wall, list);
	}

	const extraCuts: CutRow[] = [];
	const extraMaterials: MaterialRow[] = [];
	// The foder sits on the facade (`buildExteriorFacade` draws it proud of
	// the cladding). Indoors (issues 156/174) only the free-standing walls
	// carry an outer panel, so only their openings get foder — the same walls
	// `buildIndoorOuterLayers` trims.
	const indoorFreeWalls =
		input.siting === "indoor"
			? new Set<string>(freeWalls(normalizeIndoorRoom(input.indoorRoom)))
			: null;
	const trimTakeoff = calculateOpeningTrimTakeoff(
		indoorFreeWalls
			? openingsForAdjustment.filter((o) => indoorFreeWalls.has(o.wall))
			: openingsForAdjustment,
	);

	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig;
	const ENGINE_WALL_H_M = resolveWallHeightM(input.wallHeightM);
	const roof = createRectRoofGeometry({
		width: input.dimensions.width,
		length: input.dimensions.length,
		wallHeight: ENGINE_WALL_H_M,
		roofConfig,
	});
	for (const [wall, openings] of grouped.entries()) {
		if (wall === "divider") continue;
		const wallHeightMm = Math.round(
			(roofConfig.kind === "gable"
				? ENGINE_WALL_H_M
				: roof.getMaxWallHeight(wall)) * 1000,
		);
		const issues = validateWallOpenings({
			openings,
			wallLengthMm: wallLengthMm(wall, input),
			wallHeightMm,
			checkFraming: false,
			overrides: input.overrides,
		});
		if (issues.length > 0) {
			// Invalid opening data is blocked at UI-level; guard here to avoid engine throws.
			continue;
		}

		const framed = generateFramedWall(
			{
				wallLengthMm: wallLengthMm(wall, input),
				wallHeightMm,
				openings,
			},
			input.overrides,
		);

		framed.studs = framed.studs
			.map((stud) => {
				const x =
					wall === "left"
						? -input.dimensions.width / 2
						: wall === "right"
							? input.dimensions.width / 2
							: stud.xMm / 1000 - input.dimensions.width / 2;
				const z =
					wall === "front"
						? input.dimensions.length / 2
						: wall === "back"
							? -input.dimensions.length / 2
							: stud.xMm / 1000 - input.dimensions.length / 2;
				const roofTop =
					(roofConfig.kind === "gable"
						? ENGINE_WALL_H_M
						: roof.getRoofHeightAt(x, z)) *
						1000 -
					45;
				return { ...stud, yEndMm: Math.min(stud.yEndMm, Math.round(roofTop)) };
			})
			.filter((stud) => stud.yEndMm > stud.yStartMm);

		const studGroups = new Map<string, number>();
		for (const stud of framed.studs) {
			if (stud.type === "standard") continue;
			const length = stud.yEndMm - stud.yStartMm;
			const key = `${stud.type}|${length}`;
			studGroups.set(key, (studGroups.get(key) ?? 0) + 1);
		}

		for (const [key, count] of studGroups.entries()) {
			const [type, length] = key.split("|");
			extraCuts.push({
				part: `${STUD_LABELS[type] ?? type} (${wall})`,
				count: `${count} st`,
				cutLength: `${length} mm`,
				purchaseDimension: "45x95",
				pieces: [{ lengthMm: Number(length), count }],
			});
		}

		for (const header of framed.headers) {
			extraMaterials.push({
				material: `Avväxlingsbalk (${wall})`,
				dimension: `45×${header.depthMm} mm`,
				quantity: `${(header.xEndMm - header.xStartMm) / 1000} lpm`,
				phase: "framing",
				priceRuleId:
					header.depthMm >= 220
						? "prices.takbjalkar45x220"
						: header.depthMm >= 190
							? "prices.takbjalkar45x195"
							: header.depthMm >= 140
								? "prices.takbjalkar45x145"
								: "prices.kortlingRegel",
				materialKey: "framing.opening-header",
			});
			extraCuts.push({
				part: `Header ${header.depthMm}mm (${wall})`,
				count: "1 st",
				cutLength: `${header.xEndMm - header.xStartMm} mm`,
				purchaseDimension: `45x${header.depthMm}`,
				pieces: [{ lengthMm: header.xEndMm - header.xStartMm, count: 1 }],
			});
		}

		for (const sill of framed.sills) {
			extraCuts.push({
				part: `Fönsterbröstning (${wall})`,
				count: "1 st",
				cutLength: `${sill.xEndMm - sill.xStartMm} mm`,
			});
		}

		const totalFramingLm = Number(
			(
				(framed.studs
					.filter((stud) => stud.type !== "standard")
					.reduce((sum, stud) => sum + (stud.yEndMm - stud.yStartMm), 0) +
					framed.sills.reduce(
						(sum, sill) => sum + (sill.xEndMm - sill.xStartMm),
						0,
					)) /
				1000
			).toFixed(2),
		);
		const totalFramingPieces =
			framed.studs.filter((s) => s.type !== "standard").length +
			framed.sills.length;

		extraMaterials.push({
			material: `Avväxling (${wall})`,
			dimension: "45×95 mm",
			quantity: `${totalFramingPieces} st = ${totalFramingLm} lpm`,
			phase: "framing",
			priceRuleId: "prices.kortlingRegel",
		});
	}

	return {
		...base,
		materials: [...base.materials, ...extraMaterials, ...trimTakeoff.materials],
		cuts: [...base.cuts, ...extraCuts, ...trimTakeoff.cuts],
	};
}
